<template>
  <section class="board-diary-report-wrapper">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-1">
      <div class="mb-1 mb-lg-0">
        <h3 class="font-weight-bolder text-primary mb-25 d-flex align-items-center">
          <feather-icon
            icon="MapIcon"
            size="24"
            class="mr-50"
          />
          <span>{{ $t('Way') }}</span>
        </h3>
        <p class="text-muted font-small-3 mb-0">
          {{ $t('Daily journey report') }}
        </p>
      </div>

      <div
        class="d-flex align-items-center flex-wrap mt-1 mt-lg-0"
        style="gap: 8px;"
      >
        <b-button
          v-if="activeTab === 'routes'"
          :variant="showFilters ? 'primary' : 'outline-primary'"
          size="sm"
          class="d-inline-flex align-items-center"
          @click="showFilters = !showFilters"
        >
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="mr-50"
          />
          <span>{{ showFilters ? $t('Hide Filters') : $t('Filters') }}</span>
          <b-badge
            v-if="activeFiltersCount > 0"
            variant="light-danger"
            pill
            class="ml-50"
          >
            {{ activeFiltersCount }}
          </b-badge>
        </b-button>

        <b-button
          v-if="activeTab === 'routes'"
          v-b-tooltip.hover
          variant="outline-primary"
          size="sm"
          class="btn-icon"
          :title="$t('Update Data')"
          :disabled="isSearching"
          @click="updateReport"
        >
          <feather-icon
            icon="RotateCwIcon"
            size="14"
            :class="{ 'spin-icon': isSearching }"
          />
        </b-button>

        <b-button
          v-if="activeTab === 'routes' && hasRouteData"
          v-b-tooltip.hover
          variant="outline-success"
          size="sm"
          class="d-inline-flex align-items-center"
          :title="$t('Export Sheet')"
          :disabled="exporting"
          @click="exportLocationHistoryToExcel"
        >
          <b-spinner
            v-if="exporting"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="DownloadIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-sm-inline">{{ $t('Export Sheet') }}</span>
        </b-button>

        <b-button
          variant="outline-primary"
          size="sm"
          class="d-inline-flex align-items-center"
          :to="{ name: 'queries-month', params: { id: myId } }"
        >
          <feather-icon
            icon="CalendarIcon"
            size="14"
            class="mr-50"
          />
          <span class="d-none d-md-inline">{{ $t('Monthly Summary') }}</span>
        </b-button>
      </div>
    </div>

    <b-card
      no-body
      class="asset-header-card mb-1 shadow-sm"
    >
      <div class="asset-header-content p-1">
        <div
          v-if="loadingAsset"
          class="d-flex align-items-center py-50"
        >
          <b-spinner
            variant="primary"
            small
            class="mr-1"
          />
          <span class="text-muted font-small-3">{{ $t('loadingAssetInformation') }}</span>
        </div>

        <div
          v-else
          class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between w-100"
        >
          <div class="d-flex align-items-center mb-1 mb-md-0 mr-md-2">
            <b-button
              v-b-tooltip.hover
              variant="flat-secondary"
              size="sm"
              class="btn-icon rounded-circle mr-1 p-50 flex-shrink-0 back-btn"
              :title="$t('back')"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="18"
              />
            </b-button>

            <div class="asset-avatar-box mr-1 flex-shrink-0">
              <img
                :src="assetAvatar"
                :alt="assetModel || $t('Model')"
                class="asset-avatar-img"
                @error="handleImageError"
              >
            </div>

            <div class="asset-title-group">
              <div class="d-flex align-items-center flex-wrap">
                <div
                  v-if="assetPlate"
                  v-b-tooltip.hover
                  class="license-plate-badge mr-75 cursor-pointer"
                  :title="$t('clickToCopyLicensePlate')"
                  @click="copyToClipboard(assetPlate, $t('licensePlate'))"
                >
                  <span class="plate-number">{{ assetPlate }}</span>
                  <feather-icon
                    icon="CopyIcon"
                    size="11"
                    class="ml-25 text-muted copy-icon"
                  />
                </div>

                <div class="d-inline-flex align-items-baseline flex-wrap">
                  <span
                    v-if="assetManufacturer"
                    class="asset-brand-text font-weight-bold mr-50"
                  >
                    {{ assetManufacturer }}
                  </span>
                  <h5 class="mb-0 font-weight-bolder text-dark asset-model-title">
                    {{ assetModel || assetPlate || assetChassis || myId }}
                  </h5>
                </div>

                <b-badge
                  v-if="assetNickname"
                  variant="light-primary"
                  class="ml-50 font-weight-normal"
                >
                  {{ assetNickname }}
                </b-badge>
              </div>

              <div
                v-if="!assetPlate && assetChassis"
                class="d-flex align-items-center mt-25"
              >
                <small class="text-muted font-weight-bold">
                  {{ $t('chassis') }}:
                  <span
                    v-b-tooltip.hover
                    class="font-family-monospace text-primary cursor-pointer"
                    :title="$t('clickToCopyChassis')"
                    @click="copyToClipboard(assetChassis, $t('chassis'))"
                  >
                    {{ assetChassis }}
                    <feather-icon
                      icon="CopyIcon"
                      size="11"
                      class="ml-25 text-muted"
                    />
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div
            class="d-flex align-items-center flex-wrap"
            style="gap: 6px;"
          >
            <div
              v-if="assetChassis && assetPlate"
              v-b-tooltip.hover
              class="meta-chip cursor-pointer"
              :title="$t('clickToCopyChassis')"
              @click="copyToClipboard(assetChassis, $t('chassis'))"
            >
              <feather-icon
                icon="HashIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('chassis') }}:</span>
              <span class="meta-value font-family-monospace">{{ assetChassis }}</span>
              <feather-icon
                icon="CopyIcon"
                size="12"
                class="ml-50 text-muted copy-icon"
              />
            </div>

            <div
              v-if="assetGroupName"
              class="meta-chip"
            >
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('group') }}:</span>
              <span class="meta-value">{{ assetGroupName }}</span>
            </div>

            <div
              v-if="assetCustomerName"
              class="meta-chip"
            >
              <feather-icon
                icon="UserIcon"
                size="14"
                class="text-primary mr-50"
              />
              <span class="meta-label">{{ $t('customer') }}:</span>
              <span class="meta-value">{{ assetCustomerName }}</span>
            </div>

            <b-button
              v-b-tooltip.hover
              variant="outline-primary"
              size="sm"
              class="btn-view-details d-inline-flex align-items-center"
              :title="$t('goToAssetSummary')"
              @click="goToAssetSummary"
            >
              <feather-icon
                icon="ActivityIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('assetSummary') }}</span>
            </b-button>
          </div>
        </div>
      </div>
    </b-card>

    <div class="d-flex align-items-center mb-1">
      <div
        class="view-switcher"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          :class="{ active: activeTab === 'routes' }"
          :aria-selected="activeTab === 'routes'"
          @click="selectTab('routes')"
        >
          <feather-icon
            icon="NavigationIcon"
            size="14"
          />
          <span>{{ $t('Routes') }}</span>
        </button>
        <button
          type="button"
          role="tab"
          :class="{ active: activeTab === 'journey' }"
          :aria-selected="activeTab === 'journey'"
          @click="selectTab('journey')"
        >
          <feather-icon
            icon="ClockIcon"
            size="14"
          />
          <span>{{ $t('Journey') }}</span>
        </button>
      </div>
    </div>

    <div v-show="activeTab === 'routes'">
      <transition name="slide-fade">
        <b-card
          v-if="showFilters"
          no-body
          class="mb-1 shadow-sm border-0 filters-card"
        >
          <b-card-header class="d-flex justify-content-between align-items-center py-75 px-1 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="SlidersIcon"
                size="16"
                class="text-primary mr-50"
              />
              <h6 class="mb-0 font-weight-bolder">
                {{ $t('Filters') }}
              </h6>
              <b-badge
                v-if="activeFiltersCount > 0"
                variant="light-primary"
                pill
                class="ml-50"
              >
                {{ $t('{count} active', { count: activeFiltersCount }) }}
              </b-badge>
            </div>
            <div
              class="d-flex align-items-center"
              style="gap: 6px;"
            >
              <b-button
                v-b-tooltip.hover
                variant="flat-danger"
                size="sm"
                class="btn-icon"
                :title="$t('Clear Filters')"
                @click="resetFilters"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                :title="$t('Close Filters')"
                @click="showFilters = false"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                />
              </b-button>
            </div>
          </b-card-header>

          <b-card-body class="p-1">
            <div
              class="mb-1 d-flex flex-wrap align-items-center"
              style="gap: 6px;"
            >
              <small class="font-weight-bold text-muted mr-50">{{ $t('Period Shortcuts:') }}</small>
              <b-badge
                v-for="preset in periodPresets"
                :key="preset.id"
                href="javascript:void(0)"
                :variant="dateRangePreset === preset.id ? 'primary' : 'light-secondary'"
                pill
                class="cursor-pointer py-50 px-75"
                @click="setDatePreset(preset.id)"
              >
                {{ preset.label }}
              </b-badge>
            </div>

            <b-row>
              <b-col
                cols="12"
                sm="6"
                lg="4"
                class="mb-1"
              >
                <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                  <feather-icon
                    icon="CalendarIcon"
                    size="13"
                    class="mr-25 text-primary"
                  />
                  <span>{{ $t('Start Date') }}</span>
                  <span class="text-danger ml-25">*</span>
                </label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="dateStart"
                    class="form-control"
                    :config="flatpickrConfig"
                    :placeholder="$t('Select...')"
                    @on-change="onCustomDateChange"
                  />
                </b-input-group>
              </b-col>

              <b-col
                cols="12"
                sm="6"
                lg="4"
                class="mb-1"
              >
                <label class="font-small-2 font-weight-bold mb-25 text-muted d-flex align-items-center">
                  <feather-icon
                    icon="CalendarIcon"
                    size="13"
                    class="mr-25 text-primary"
                  />
                  <span>{{ $t('End Date') }}</span>
                  <span class="text-danger ml-25">*</span>
                </label>
                <b-input-group size="sm">
                  <b-input-group-prepend is-text>
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                    />
                  </b-input-group-prepend>
                  <flat-pickr
                    v-model="dateEnd"
                    class="form-control"
                    :config="flatpickrConfig"
                    :placeholder="$t('Select...')"
                    @on-change="onCustomDateChange"
                  />
                </b-input-group>
              </b-col>

              <b-col
                cols="12"
                lg="4"
                class="mb-1 d-flex align-items-end"
              >
                <b-form-checkbox
                  :checked="showAddresses"
                  switch
                  class="mb-50"
                  @change="onAddressToggle"
                >
                  {{ $t('Show addresses') }}
                </b-form-checkbox>
              </b-col>
            </b-row>

            <div class="d-flex align-items-center justify-content-between flex-wrap mt-50 pt-75 border-top">
              <div class="text-muted font-small-2 d-flex align-items-center mb-50 mb-md-0 mr-1">
                <feather-icon
                  icon="InfoIcon"
                  size="14"
                  class="mr-50 text-primary flex-shrink-0"
                />
                <span>
                  {{ $t('Vehicle:') }}
                  <strong class="text-primary">{{ assetModel || assetPlate || assetChassis || myId }}</strong>.
                  {{ $t('Default hours hint') }}
                </span>
              </div>
              <div
                class="d-flex align-items-center"
                style="gap: 8px;"
              >
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="resetFilters"
                >
                  {{ $t('Restore Default') }}
                </b-button>
                <b-button
                  variant="primary"
                  size="sm"
                  class="d-inline-flex align-items-center"
                  :disabled="isSearching || !dateStart || !dateEnd"
                  @click="updateReport"
                >
                  <b-spinner
                    v-if="isSearching"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    v-else
                    icon="SearchIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span>{{ $t('Apply Filters') }}</span>
                </b-button>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </transition>

      <div
        v-if="!showFilters"
        class="px-1 py-75 mb-1 border rounded bg-light-secondary d-flex flex-wrap align-items-center shadow-xs"
        style="gap: 6px;"
      >
        <small class="font-weight-bold text-muted mr-50">{{ $t('Active Filters:') }}</small>
        <b-badge
          variant="light-primary"
          pill
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="LockIcon"
            size="12"
            class="mr-25"
          />
          {{ assetModel || assetPlate || assetChassis || myId }}
        </b-badge>
        <b-badge
          v-if="periodLabel"
          variant="light-primary"
          pill
          class="d-inline-flex align-items-center cursor-pointer"
          @click="showFilters = true"
        >
          <feather-icon
            icon="CalendarIcon"
            size="12"
            class="mr-25"
          />
          {{ periodLabel }}
        </b-badge>
        <b-badge
          v-if="showAddresses"
          variant="light-info"
          pill
          class="d-inline-flex align-items-center"
        >
          <feather-icon
            icon="MapPinIcon"
            size="12"
            class="mr-25"
          />
          {{ $t('Show addresses') }}
        </b-badge>
        <b-link
          class="font-small-2 font-weight-bold ml-50 text-danger"
          @click="resetFilters"
        >
          {{ $t('Clear') }}
        </b-link>
      </div>

      <b-row
        v-if="showKpis"
        class="match-height mb-1"
      >
        <b-col
          v-for="kpi in kpiCards"
          :key="kpi.key"
          cols="6"
          xl="3"
          class="mb-1"
        >
          <b-card
            no-body
            class="kpi-card shadow-sm border-0 h-100 p-1"
          >
            <template v-if="kpiLoading">
              <b-skeleton
                width="55%"
                height="12px"
                class="mb-50"
              />
              <b-skeleton
                width="70%"
                height="28px"
              />
            </template>
            <template v-else>
              <div class="d-flex justify-content-between align-items-start">
                <div class="min-width-0">
                  <span class="text-muted font-small-1 font-weight-bolder text-uppercase">
                    {{ kpi.label }}
                  </span>
                  <h3
                    class="font-weight-bolder mb-0 mt-25 text-truncate"
                    :class="kpi.valueClass"
                  >
                    {{ kpi.value }}
                    <small
                      v-if="kpi.unit"
                      class="kpi-unit"
                    >{{ kpi.unit }}</small>
                  </h3>
                </div>
                <b-avatar
                  :variant="kpi.variant"
                  size="38px"
                  class="flex-shrink-0"
                >
                  <feather-icon
                    :icon="kpi.icon"
                    size="18"
                  />
                </b-avatar>
              </div>
              <div class="font-small-1 text-muted mt-50 pt-50 border-top text-truncate">
                {{ kpi.hint }}
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
          :xl="showAddresses ? 7 : 12"
          class="mb-1"
        >
          <b-card
            no-body
            class="shadow-sm border-0 map-card"
            :class="{ 'is-fullscreen': isMapFullscreen }"
          >
            <div class="section-card-header">
              <div class="min-width-0">
                <h5 class="mb-0 font-weight-bolder">
                  {{ $t('Location') }}
                </h5>
                <small class="text-muted">
                  <template v-if="hasRouteData">
                    {{ routeData.length }} {{ $t('Points') }}
                  </template>
                  <template v-else-if="isSearching">
                    {{ $t('Generating report') }}
                  </template>
                  <template v-else>
                    {{ voidMessage }}
                  </template>
                </small>
              </div>
              <div
                class="d-flex align-items-center flex-wrap"
                style="gap: 8px;"
              >
                <div class="d-none d-md-flex align-items-center font-small-2 text-muted map-legend">
                  <span class="legend-dot legend-start" />
                  {{ $t('Start') }}
                  <span class="legend-dot legend-end" />
                  {{ $t('End') }}
                  <span class="legend-cluster">+</span>
                  {{ $t('Grouped') }}
                </div>
                <b-button-group size="sm">
                  <b-button
                    :variant="btnMap ? 'primary' : 'outline-primary'"
                    @click="chooseLayer('map')"
                  >
                    {{ $t('Map') }}
                  </b-button>
                  <b-button
                    :variant="btnGround ? 'primary' : 'outline-primary'"
                    @click="chooseLayer('ground')"
                  >
                    {{ $t('Ground') }}
                  </b-button>
                </b-button-group>
                <b-button
                  size="sm"
                  :variant="showAddresses ? 'primary' : 'outline-primary'"
                  class="d-inline-flex align-items-center"
                  @click="toggleAddresses"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="mr-50"
                  />
                  <span class="d-none d-sm-inline">{{ showAddresses ? $t('Hide address') : $t('Show addresses') }}</span>
                </b-button>
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  :variant="isMapFullscreen ? 'primary' : 'outline-primary'"
                  class="d-inline-flex align-items-center flex-shrink-0"
                  :title="isMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')"
                  @click="toggleMapFullscreen"
                >
                  <feather-icon
                    :icon="isMapFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'"
                    size="14"
                    class="mr-50"
                  />
                  <span class="d-none d-sm-inline">
                    {{ isMapFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen') }}
                  </span>
                </b-button>
              </div>
            </div>

            <div class="map-stage">
              <div
                v-if="isSearching"
                class="map-status"
              >
                <b-spinner
                  small
                  variant="primary"
                />
                <span>{{ loadingAddress ? $t('Searching addresses') : $t('Generating report') }}</span>
              </div>

              <div
                v-if="!isSearching && !hasRouteData"
                class="map-empty"
              >
                <b-avatar
                  variant="light-warning"
                  size="56px"
                  class="mb-1"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="26"
                    class="text-warning"
                  />
                </b-avatar>
                <h5 class="font-weight-bolder mb-25">
                  {{ $t('No matching information found') }}
                </h5>
                <b-button
                  variant="primary"
                  size="sm"
                  @click="showFilters = true"
                >
                  {{ $t('Filters') }}
                </b-button>
              </div>

              <l-map
                :key="'route-map-' + mapKey"
                ref="myMapRoute"
                class="route-map"
                :zoom="zoom"
                :center="center"
              >
                <l-tile-layer :url="mapUrl" />
                <l-polyline
                  :lat-lngs="latlngs"
                  color="#ffffff"
                  :weight="8"
                  :opacity="0.92"
                />
                <l-polyline
                  :lat-lngs="latlngs"
                  :color="polylineColor"
                  :weight="4"
                  :opacity="0.95"
                />
                <marker-cluster
                  v-if="clusteredMarkers.length"
                  :options="clusterOptions"
                >
                  <l-marker
                    v-for="marker in clusteredMarkers"
                    :key="'pin-' + marker.index"
                    :lat-lng="[Number(marker.pin.lat), Number(marker.pin.lng)]"
                    :icon="routeIcon(marker)"
                    @click="focusPoint(marker.pin, marker.index)"
                  >
                    <l-tooltip :options="markerTooltipOptions">
                      <div class="font-small-2 route-tooltip">
                        <div class="font-weight-bolder mb-25">
                          #{{ marker.index + 1 }}
                        </div>
                        <div>{{ $t('Day') }}: <strong>{{ formatPointDate(marker.pin.dateTime) }}</strong></div>
                        <div>{{ $t('Time') }}: <strong>{{ formatPointTime(marker.pin.dateTime) }}</strong></div>
                        <div>{{ $t('Speed') }}: <strong>{{ formatSpeed(marker.pin.kmh) }}</strong></div>
                      </div>
                    </l-tooltip>
                  </l-marker>
                </marker-cluster>
                <l-marker
                  v-for="marker in anchorMarkers"
                  :key="'anchor-' + marker.index"
                  :lat-lng="[Number(marker.pin.lat), Number(marker.pin.lng)]"
                  :icon="routeIcon(marker)"
                  :z-index-offset="markerZIndex(marker)"
                  @click="focusPoint(marker.pin, marker.index)"
                >
                  <l-tooltip :options="markerTooltipOptions">
                    <div class="font-small-2 route-tooltip">
                      <div class="font-weight-bolder mb-25">
                        #{{ marker.index + 1 }}
                      </div>
                      <div>{{ $t('Day') }}: <strong>{{ formatPointDate(marker.pin.dateTime) }}</strong></div>
                      <div>{{ $t('Time') }}: <strong>{{ formatPointTime(marker.pin.dateTime) }}</strong></div>
                      <div>{{ $t('Speed') }}: <strong>{{ formatSpeed(marker.pin.kmh) }}</strong></div>
                    </div>
                  </l-tooltip>
                </l-marker>
              </l-map>
              <div
                v-if="hasRouteData && clusteredMarkers.length"
                class="map-cluster-hint"
              >
                {{ $t('Nearby points are grouped. Zoom in or click a group to separate them.') }}
              </div>
            </div>
          </b-card>
        </b-col>

        <b-col
          v-if="showAddresses"
          cols="12"
          xl="5"
          class="mb-1"
        >
          <b-card
            no-body
            class="shadow-sm border-0 address-card h-100"
          >
            <div class="section-card-header">
              <div>
                <h5 class="mb-0 font-weight-bolder">
                  {{ $t('Address') }}
                </h5>
                <small class="text-muted">{{ routeData.length }} {{ $t('Points') }}</small>
              </div>
            </div>

            <div class="address-scroll">
              <div
                v-if="loadingAddress && !hasRouteData"
                class="text-center py-3"
              >
                <b-spinner
                  variant="primary"
                  class="mb-1"
                />
                <div class="text-muted font-small-3">
                  {{ $t('Searching addresses') }}
                </div>
              </div>

              <div
                v-else-if="!hasRouteData"
                class="text-center py-3"
              >
                <h5 class="font-weight-bolder mb-25">
                  {{ $t('no data') }}
                </h5>
                <p class="text-muted font-small-3 mb-0">
                  {{ voidMessage }}
                </p>
              </div>

              <template v-else>
                <button
                  v-for="(pin, index) in routeData"
                  :key="'stop-' + index"
                  type="button"
                  class="route-stop"
                  :class="{ active: selectedIndex === index }"
                  @click="focusPoint(pin, index)"
                >
                  <span
                    class="route-stop-index"
                    :class="{
                      'is-start': index === 0,
                      'is-end': index === routeData.length - 1,
                    }"
                  >
                    {{ index + 1 }}
                  </span>
                  <div class="min-width-0 flex-grow-1">
                    <div class="d-flex align-items-center justify-content-between mb-25">
                      <strong>{{ formatPointTime(pin.dateTime) }}</strong>
                      <b-badge variant="light-primary">
                        {{ formatSpeed(pin.kmh) }}
                      </b-badge>
                    </div>
                    <small class="text-muted d-block">
                      {{ formatPointDate(pin.dateTime) }}
                    </small>
                    <div class="font-small-2 mt-25 address-line">
                      {{ pointAddress(pin) }}
                    </div>
                  </div>
                </button>

                <div class="text-center pt-50">
                  <b-button
                    v-if="!loadingAddress && currentRequestLength === 10"
                    variant="primary"
                    size="sm"
                    @click="loadMoreAddresses"
                  >
                    {{ $t('Load more') }}
                  </b-button>
                  <div
                    v-else-if="loadingAddress"
                    class="text-muted font-small-3"
                  >
                    <b-spinner
                      small
                      variant="primary"
                      class="mr-50"
                    />
                    {{ $t('Searching addresses') }}
                  </div>
                </div>
              </template>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <report-journey
      v-if="activeTab === 'journey'"
      :my-id="String(myId || '')"
    />

    <b-modal
      v-model="showExportModal"
      size="sm"
      centered
      hide-header
      hide-footer
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
    >
      <div class="mx-2 my-1 text-center">
        <h4 class="font-weight-bolder mb-1">
          {{ $t('Preparing report') }}...
        </h4>
        <b-spinner
          variant="primary"
          :label="$t('Loading')"
        />
      </div>
    </b-modal>
  </section>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from '@vue/composition-api'
import {
  BAvatar,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCardHeader,
  BCol,
  BFormCheckbox,
  BInputGroup,
  BInputGroupPrepend,
  BLink,
  BModal,
  BRow,
  BSkeleton,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import {
  LMap,
  LMarker,
  LPolyline,
  LTileLayer,
  LTooltip,
} from 'vue2-leaflet'
import MarkerCluster from 'vue2-leaflet-markercluster'
import L from 'leaflet'
import markerIconUrl from 'leaflet/dist/images/marker-icon.png'
import markerIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png'
import flatPickr from 'vue-flatpickr-component'
import 'leaflet/dist/leaflet.css'
import 'leaflet-polylinedecorator'
import store from '@/store'
import router from '@/router'
import { getIntlLocale } from '@/libs/i18n/dateLocale'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import { resolveIcon } from '@/utils/assetUtils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import queriesStoreModule from '../queriesStoreModule'
import ReportJourney from './components/ReportJourney.vue'

// O webpack não resolve o caminho relativo do ícone padrão do Leaflet.
// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIconRetinaUrl,
  shadowUrl: markerShadowUrl,
})

const STORE_MODULE_NAME = 'app-queries'
const MAP_LAYER_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const GROUND_LAYER_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const POLYLINE_COLOR = '#7367f0'
const ADDRESS_PAGE_SIZE = 10
const MAX_HISTORY_PAGES = 400
const MARKER_SAMPLE_LIMIT = 500
const routeIconCache = {}

function clusterRadiusForZoom(zoom) {
  if (zoom >= 18) return 22
  if (zoom >= 16) return 34
  if (zoom >= 14) return 48
  return 68
}

function clusterTier(count) {
  if (count >= 80) return 'lg'
  if (count >= 20) return 'md'
  return 'sm'
}

function clusterSize(tier) {
  if (tier === 'lg') return 46
  if (tier === 'md') return 38
  return 32
}

function createClusterIcon(cluster) {
  const count = cluster.getChildCount()
  const tier = clusterTier(count)
  const size = clusterSize(tier)
  return L.divIcon({
    className: `route-cluster route-cluster-${tier}`,
    html: `<span>${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

export default {
  name: 'GraphicBoardDiary',
  components: {
    ReportJourney,
    BAvatar,
    BBadge,
    BButton,
    BButtonGroup,
    BCard,
    BCardBody,
    BCardHeader,
    BCol,
    BFormCheckbox,
    BInputGroup,
    BInputGroupPrepend,
    BLink,
    BModal,
    BRow,
    BSkeleton,
    BSpinner,
    LMap,
    LMarker,
    LPolyline,
    LTileLayer,
    LTooltip,
    MarkerCluster,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t ? instance.proxy.$t.bind(instance.proxy) : value => value
    const myId = router.currentRoute.params.id
    const fallbackAvatar = resolveIcon(null, null)

    const journeyQuery = router.currentRoute.query.journey
    const activeTab = ref(String(journeyQuery) === '0' ? 'routes' : 'journey')
    const showFilters = ref(false)
    const showAddresses = ref(false)
    const isMapFullscreen = ref(false)
    const showExportModal = ref(false)

    const loadingAsset = ref(false)
    const loadingHistory = ref(activeTab.value === 'routes')
    const loadingAddress = ref(false)
    const exporting = ref(false)
    const assetData = ref(null)

    function pad(value) {
      return String(value).padStart(2, '0')
    }

    function toPickerValue(date) {
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    function startOfDay(date) {
      const copy = new Date(date)
      copy.setHours(0, 0, 0, 0)
      return copy
    }

    function buildDefaultRange() {
      const start = startOfDay(new Date())
      start.setDate(start.getDate() - 1)
      return {
        start: toPickerValue(start),
        end: toPickerValue(new Date()),
      }
    }

    function rangeForPreset(preset) {
      const now = new Date()
      if (preset === 'today') {
        return { start: toPickerValue(startOfDay(now)), end: toPickerValue(now) }
      }
      if (preset === 'yesterday') {
        const day = new Date(now)
        day.setDate(day.getDate() - 1)
        const start = startOfDay(day)
        const end = new Date(start)
        end.setHours(23, 59, 0, 0)
        return { start: toPickerValue(start), end: toPickerValue(end) }
      }
      if (preset === 'last7') {
        const start = startOfDay(now)
        start.setDate(start.getDate() - 6)
        return { start: toPickerValue(start), end: toPickerValue(now) }
      }
      const start = startOfDay(now)
      start.setDate(1)
      return { start: toPickerValue(start), end: toPickerValue(now) }
    }

    function formatDisplay(value) {
      if (!value) return ''
      const [datePart, timePart = ''] = String(value).split(' ')
      const [year, month, day] = datePart.split('-')
      if (!year || !month || !day) return String(value)
      return timePart ? `${day}/${month}/${year} ${timePart}` : `${day}/${month}/${year}`
    }

    function splitDateHour(value) {
      const [date = '', hour = '00:00'] = String(value || '').trim().split(' ')
      return { date, hour: hour.slice(0, 5) }
    }

    function formatPointDate(dateString) {
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(getIntlLocale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    function formatPointTime(dateString) {
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleTimeString(getIntlLocale(), {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    function formatSpeed(value) {
      const speed = Number(value)
      if (!Number.isFinite(speed)) return '—'
      return `${speed} Km/h`
    }

    function pointAddress(pin) {
      if (pin?.addressComplete) return pin.addressComplete
      return `Lat: ${pin?.lat}, Lng: ${pin?.lng}`
    }

    const defaultRange = buildDefaultRange()
    const dateStart = ref(defaultRange.start)
    const dateEnd = ref(defaultRange.end)
    const dateRangePreset = ref('')
    const flatpickrConfig = {
      enableTime: true,
      time_24hr: true,
      dateFormat: 'Y-m-d H:i',
      altInput: true,
      altFormat: 'd/m/Y H:i',
      altInputClass: 'form-control form-control-sm',
      locale: getFlatpickrLocale(),
    }

    const routeData = ref([])
    const latlngs = ref([])
    const addressPage = ref(1)
    const currentRequestLength = ref(0)
    const voidMessage = ref($t('No matching information found'))
    const selectedIndex = ref(null)
    const mapKey = ref(1)
    const zoom = ref(4)
    const center = ref([-10.1500034, -67.9163393])
    const mapUrl = ref(MAP_LAYER_URL)
    const btnMap = ref(true)
    const btnGround = ref(false)
    const polylineColor = POLYLINE_COLOR

    let searchSeq = 0
    let ignoreDateChange = false
    let decoratorLayer = null

    const assetPlate = computed(() => assetData.value?.plate || '')
    const assetChassis = computed(() => assetData.value?.chassisNumber || '')
    const assetManufacturer = computed(() => (
      assetData.value?.manufacturerName
      || assetData.value?.brandName
      || assetData.value?.manufacturer
      || ''
    ))
    const assetModel = computed(() => {
      if (!assetData.value) return ''
      const name = assetData.value.modelIdName || assetData.value.modelName || ''
      const year = assetData.value.modelYear ? `(${assetData.value.modelYear})` : ''
      return `${name} ${year}`.trim()
    })
    const assetNickname = computed(() => assetData.value?.nickname || '')
    const assetGroupName = computed(() => assetData.value?.assetGroupName || assetData.value?.groupName || '')
    const assetCustomerName = computed(() => assetData.value?.customerName || assetData.value?.ownerName || '')
    const assetAvatar = computed(() => {
      if (!assetData.value) return fallbackAvatar
      return resolveIcon(assetData.value.assetType, assetData.value.iconUrl)
    })

    const hasRouteData = computed(() => routeData.value.length > 0)
    const isSearching = computed(() => loadingHistory.value || loadingAddress.value)
    const showKpis = computed(() => isSearching.value || hasRouteData.value)
    const kpiLoading = computed(() => isSearching.value && !hasRouteData.value)
    const activeFiltersCount = computed(() => {
      let count = 0
      if (dateStart.value && dateEnd.value) count += 1
      if (showAddresses.value) count += 1
      return count
    })
    const periodLabel = computed(() => {
      if (!dateStart.value && !dateEnd.value) return ''
      return `${formatDisplay(dateStart.value)} — ${formatDisplay(dateEnd.value)}`
    })
    const periodPresets = computed(() => ([
      { id: 'today', label: $t('Today') },
      { id: 'yesterday', label: $t('Yesterday') },
      { id: 'last7', label: $t('Last 7 days') },
      { id: 'month', label: $t('This Month') },
    ]))

    const firstPoint = computed(() => routeData.value[0] || null)
    const lastPoint = computed(() => routeData.value[routeData.value.length - 1] || null)
    const maxSpeed = computed(() => {
      let max = null
      routeData.value.forEach(point => {
        const speed = Number(point.kmh)
        if (Number.isFinite(speed) && (max === null || speed > max)) max = speed
      })
      return max
    })
    const kpiCards = computed(() => ([
      {
        key: 'points',
        label: $t('Points'),
        value: hasRouteData.value ? String(routeData.value.length) : '—',
        unit: '',
        hint: $t('Location'),
        icon: 'MapPinIcon',
        variant: 'light-primary',
        valueClass: 'text-primary',
      },
      {
        key: 'speed',
        label: $t('Max Speed'),
        value: maxSpeed.value === null ? '—' : String(maxSpeed.value),
        unit: maxSpeed.value === null ? '' : 'Km/h',
        hint: $t('Speed'),
        icon: 'ZapIcon',
        variant: 'light-warning',
        valueClass: 'text-warning',
      },
      {
        key: 'start',
        label: $t('Start'),
        value: firstPoint.value ? formatPointTime(firstPoint.value.dateTime) : '—',
        unit: '',
        hint: firstPoint.value ? formatPointDate(firstPoint.value.dateTime) : $t('Start Date'),
        icon: 'PlayCircleIcon',
        variant: 'light-success',
        valueClass: 'text-success',
      },
      {
        key: 'end',
        label: $t('End'),
        value: lastPoint.value ? formatPointTime(lastPoint.value.dateTime) : '—',
        unit: '',
        hint: lastPoint.value ? formatPointDate(lastPoint.value.dateTime) : $t('End Date'),
        icon: 'FlagIcon',
        variant: 'light-danger',
        valueClass: 'text-danger',
      },
    ]))

    const plottedMarkers = computed(() => {
      const points = routeData.value
      const total = points.length
      if (!total) return []
      const indexes = new Set([0, total - 1])
      if (selectedIndex.value !== null && selectedIndex.value >= 0 && selectedIndex.value < total) {
        indexes.add(selectedIndex.value)
      }
      const step = total <= MARKER_SAMPLE_LIMIT ? 1 : Math.ceil(total / MARKER_SAMPLE_LIMIT)
      for (let index = 0; index < total; index += step) indexes.add(index)
      return Array.from(indexes)
        .sort((a, b) => a - b)
        .map(index => ({
          index,
          pin: points[index],
          anchor: index === 0 || index === total - 1 || index === selectedIndex.value,
        }))
    })
    const clusteredMarkers = computed(() => plottedMarkers.value.filter(marker => !marker.anchor))
    const anchorMarkers = computed(() => plottedMarkers.value.filter(marker => marker.anchor))
    const markerTooltipOptions = {
      direction: 'top',
      offset: [0, -8],
      opacity: 0.97,
    }
    const clusterOptions = {
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      spiderfyDistanceMultiplier: 1.55,
      chunkedLoading: true,
      removeOutsideVisibleBounds: true,
      animate: true,
      maxClusterRadius: clusterRadiusForZoom,
      spiderLegPolylineOptions: {
        weight: 1.5,
        color: POLYLINE_COLOR,
        opacity: 0.55,
      },
      iconCreateFunction: createClusterIcon,
    }

    function showSnackbar({ title, icon, variant = 'info' }) {
      const proxy = instance?.proxy
      if (!proxy?.$toast) return
      proxy.$toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    function copyToClipboard(text, label) {
      if (!text) return
      const successTitle = `${label || $t('text')} ${$t('copiedToClipboard')}!`
      const failTitle = $t('failedToCopyToClipboard')
      const notifySuccess = () => showSnackbar({ title: successTitle, icon: 'CheckIcon', variant: 'success' })
      const notifyError = () => showSnackbar({ title: failTitle, icon: 'AlertTriangleIcon', variant: 'danger' })
      const proxy = instance?.proxy
      if (proxy?.$copyText) {
        proxy.$copyText(text).then(notifySuccess).catch(notifyError)
        return
      }
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(notifySuccess).catch(notifyError)
      }
    }

    function handleImageError(event) {
      const image = event && event.target
      if (image) image.src = fallbackAvatar
    }

    function goBack() {
      router.back()
    }

    function goToAssetSummary() {
      if (!myId) return
      router.push({ name: 'apps-asset-summary', params: { id: myId } })
    }

    function setDatePreset(preset) {
      ignoreDateChange = true
      const range = rangeForPreset(preset)
      dateStart.value = range.start
      dateEnd.value = range.end
      dateRangePreset.value = preset
      setTimeout(() => {
        ignoreDateChange = false
      }, 300)
    }

    function onCustomDateChange() {
      if (ignoreDateChange) return
      dateRangePreset.value = ''
    }

    function chooseLayer(layer) {
      const selected = layer === 'ground' ? 'ground' : 'map'
      try {
        localStorage.layerPreview = selected
      } catch (e) {
        // armazenamento local indisponível
      }
      if (selected === 'ground') {
        btnGround.value = true
        btnMap.value = false
        mapUrl.value = GROUND_LAYER_URL
        return
      }
      btnGround.value = false
      btnMap.value = true
      mapUrl.value = MAP_LAYER_URL
    }

    function responseIsValid(response) {
      return Boolean(response && response.data && Array.isArray(response.data.data) && response.data.data.length > 0)
    }

    function coordinateIsValid(latitude, longitude) {
      const lat = parseFloat(latitude)
      const lng = parseFloat(longitude)
      const latitudeValid = !Number.isNaN(lat) && lat >= -90 && lat <= 90 && lat !== 0
      const longitudeValid = !Number.isNaN(lng) && lng >= -180 && lng <= 180 && lng !== 0
      return latitudeValid && longitudeValid
    }

    function clearDecorator() {
      if (!decoratorLayer) return
      try {
        decoratorLayer.remove()
      } catch (e) {
        // o mapa pode já ter sido destruído
      }
      decoratorLayer = null
    }

    function clearReport() {
      currentRequestLength.value = 0
      addressPage.value = 1
      routeData.value = []
      latlngs.value = []
      selectedIndex.value = null
      clearDecorator()
    }

    function appendLocations(locations) {
      locations.forEach(location => {
        if (!coordinateIsValid(location.lat, location.lng)) return
        routeData.value.push(location)
        latlngs.value.push([Number(location.lat), Number(location.lng)])
      })
    }

    function simplifyPolyline(points, maxPoints = 250) {
      if (points.length <= maxPoints) return points
      const step = Math.ceil(points.length / maxPoints)
      const simplified = points.filter((_, index) => index % step === 0)
      const last = points[points.length - 1]
      if (simplified[simplified.length - 1] !== last) simplified.push(last)
      return simplified
    }

    function drawRouteOnMap(remount = false) {
      if (!latlngs.value.length) return
      if (remount) mapKey.value += 1
      nextTick(() => {
        const map = instance?.proxy?.$refs?.myMapRoute?.mapObject
        if (!map || !latlngs.value.length) return
        map.invalidateSize()
        try {
          map.fitBounds(latlngs.value, { padding: [40, 40] })
        } catch (e) {
          // bounds inválidos
        }
        clearDecorator()
        if (!L.polylineDecorator || !L.Symbol) return
        const simplified = simplifyPolyline(routeData.value)
        const line = L.polyline(simplified.map(point => [Number(point.lat), Number(point.lng)]))
        decoratorLayer = L.polylineDecorator(line, {
          patterns: [{
            offset: 12,
            repeat: 80,
            symbol: L.Symbol.arrowHead({
              pixelSize: 10,
              pathOptions: {
                fillOpacity: 1,
                weight: 0,
                color: POLYLINE_COLOR,
              },
            }),
          }],
        }).addTo(map)
      })
    }

    async function fetchRouteHistory(page) {
      const start = splitDateHour(dateStart.value)
      const end = splitDateHour(dateEnd.value)
      try {
        return await store.dispatch(`${STORE_MODULE_NAME}/fetchRouteHistory`, {
          id: myId,
          getAddress: showAddresses.value,
          startDate: start.date,
          endDate: end.date,
          startHour: start.hour,
          endHour: end.hour,
          page,
        })
      } catch (e) {
        voidMessage.value = $t('No matching information found')
        clearReport()
        showSnackbar({
          title: $t('An unexpected error occurred'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
        return null
      }
    }

    async function composeReport() {
      searchSeq += 1
      const seq = searchSeq
      voidMessage.value = $t('No matching information found')
      clearReport()
      loadingAddress.value = false
      loadingHistory.value = true
      let pageNumber = 1
      try {
        while (pageNumber <= MAX_HISTORY_PAGES) {
          if (seq !== searchSeq) return
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchRouteHistory(pageNumber)
          if (seq !== searchSeq) return
          if (!responseIsValid(response)) break
          appendLocations(response.data.data)
          pageNumber += 1
        }
      } finally {
        if (seq === searchSeq) {
          loadingHistory.value = false
          if (latlngs.value.length) drawRouteOnMap(true)
        }
      }
    }

    async function composeReportWithAddress() {
      searchSeq += 1
      const seq = searchSeq
      loadingHistory.value = false
      loadingAddress.value = true
      try {
        const response = await fetchRouteHistory(addressPage.value)
        if (seq !== searchSeq) return
        currentRequestLength.value = Array.isArray(response?.data?.data) ? response.data.data.length : 0
        if (!responseIsValid(response)) return
        addressPage.value += 1
        appendLocations(response.data.data)
        if (latlngs.value.length) drawRouteOnMap(addressPage.value === 2)
      } finally {
        if (seq === searchSeq) loadingAddress.value = false
      }
    }

    function ensureDateOrder() {
      if (dateStart.value && dateEnd.value && String(dateStart.value) > String(dateEnd.value)) {
        const swap = dateStart.value
        dateStart.value = dateEnd.value
        dateEnd.value = swap
      }
    }

    function updateReport() {
      if (!dateStart.value || !dateEnd.value) {
        showFilters.value = true
        return
      }
      ensureDateOrder()
      if (showAddresses.value) {
        clearReport()
        addressPage.value = 1
        composeReportWithAddress()
        return
      }
      composeReport()
    }

    function refreshMapSize() {
      nextTick(() => {
        const map = instance?.proxy?.$refs?.myMapRoute?.mapObject
        if (!map) return
        map.invalidateSize()
        if (!latlngs.value.length) return
        try {
          map.fitBounds(latlngs.value, { padding: [40, 40] })
        } catch (e) {
          // bounds inválidos
        }
      })
    }

    function toggleMapFullscreen() {
      isMapFullscreen.value = !isMapFullscreen.value
      document.body.style.overflow = isMapFullscreen.value ? 'hidden' : ''
      setTimeout(refreshMapSize, 60)
    }

    function onMapFullscreenKeydown(event) {
      if (event.key === 'Escape' && isMapFullscreen.value) toggleMapFullscreen()
    }

    function selectTab(tab) {
      if (activeTab.value === tab) return
      activeTab.value = tab
      router.replace({
        name: 'queries-telemetry',
        params: { id: myId },
        query: { journey: tab === 'routes' ? 0 : 1 },
      }).catch(() => {})

      if (tab !== 'routes') {
        if (isMapFullscreen.value) {
          isMapFullscreen.value = false
          document.body.style.overflow = ''
        }
        return
      }
      nextTick(() => {
        const map = instance?.proxy?.$refs?.myMapRoute?.mapObject
        if (map) map.invalidateSize()
      })
      if (!routeData.value.length && !isSearching.value) updateReport()
    }

    function loadMoreAddresses() {
      if (loadingAddress.value || currentRequestLength.value !== ADDRESS_PAGE_SIZE) return
      composeReportWithAddress()
    }

    function onAddressToggle(value) {
      showAddresses.value = Boolean(value)
      updateReport()
    }

    function toggleAddresses() {
      onAddressToggle(!showAddresses.value)
    }

    function resetFilters() {
      const range = buildDefaultRange()
      dateStart.value = range.start
      dateEnd.value = range.end
      dateRangePreset.value = ''
      showAddresses.value = false
      updateReport()
    }

    function routeIcon(marker) {
      const total = routeData.value.length
      const { index } = marker
      const selected = index === selectedIndex.value
      let variant = 'dot'
      let size = 11
      if (selected) {
        variant = 'selected'
        size = 28
      } else if (index === 0) {
        variant = 'start'
        size = 24
      } else if (total > 1 && index === total - 1) {
        variant = 'end'
        size = 24
      }
      const label = variant === 'dot' ? '' : String(index + 1)
      const cacheKey = `${variant}-${size}-${label}`
      if (!routeIconCache[cacheKey]) {
        routeIconCache[cacheKey] = L.divIcon({
          className: `route-pin route-pin-${variant}`,
          html: `<span>${label}</span>`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        })
      }
      return routeIconCache[cacheKey]
    }

    function markerZIndex(marker) {
      if (marker.index === selectedIndex.value) return 2000
      return 1400
    }

    function focusPoint(pin, index) {
      selectedIndex.value = index
      nextTick(() => {
        const map = instance?.proxy?.$refs?.myMapRoute?.mapObject
        if (map && pin?.lat != null && pin?.lng != null) {
          const nextZoom = Math.max(map.getZoom(), 17)
          map.flyTo([Number(pin.lat), Number(pin.lng)], nextZoom, { duration: 0.45 })
        }
        const activeStop = instance?.proxy?.$el?.querySelector('.route-stop.active')
        if (activeStop && typeof activeStop.scrollIntoView === 'function') {
          activeStop.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    }

    async function fetchAsset() {
      if (!myId) return
      loadingAsset.value = true
      try {
        const response = await store.dispatch(`${STORE_MODULE_NAME}/fetchAsset`, { id: myId })
        assetData.value = response?.data || null
      } catch (e) {
        assetData.value = null
      } finally {
        loadingAsset.value = false
      }
    }

    async function exportLocationHistoryToExcel() {
      if (!hasRouteData.value || exporting.value) return
      exporting.value = true
      showExportModal.value = true
      try {
        const start = splitDateHour(dateStart.value)
        const end = splitDateHour(dateEnd.value)
        const response = await store.dispatch(`${STORE_MODULE_NAME}/exportLocationHistoryToExcel`, {
          id: myId,
          startDate: start.date,
          endDate: end.date,
          startHour: start.hour,
          endHour: end.hour,
          getAddress: showAddresses.value,
        })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `route_${start.date || 'report'}.xls`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        showSnackbar({
          title: $t('An unexpected error occurred'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        })
      } finally {
        exporting.value = false
        showExportModal.value = false
      }
    }

    function restoreSavedLayer() {
      try {
        if (localStorage.layerPreview === 'ground') chooseLayer('ground')
      } catch (e) {
        // armazenamento local indisponível
      }
    }

    onMounted(() => {
      document.addEventListener('keydown', onMapFullscreenKeydown)
      if (!store.hasModule(STORE_MODULE_NAME)) {
        store.registerModule(STORE_MODULE_NAME, queriesStoreModule)
      }
      restoreSavedLayer()
      fetchAsset()
      if (activeTab.value === 'routes') updateReport()
    })

    onUnmounted(() => {
      document.removeEventListener('keydown', onMapFullscreenKeydown)
      document.body.style.overflow = ''
      clearDecorator()
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    return {
      myId,
      activeTab,
      showFilters,
      showAddresses,
      isMapFullscreen,
      showExportModal,
      loadingAsset,
      loadingHistory,
      loadingAddress,
      exporting,
      isSearching,
      dateStart,
      dateEnd,
      dateRangePreset,
      flatpickrConfig,
      routeData,
      latlngs,
      currentRequestLength,
      voidMessage,
      selectedIndex,
      mapKey,
      zoom,
      center,
      mapUrl,
      btnMap,
      btnGround,
      polylineColor,
      assetPlate,
      assetChassis,
      assetManufacturer,
      assetModel,
      assetNickname,
      assetGroupName,
      assetCustomerName,
      assetAvatar,
      hasRouteData,
      showKpis,
      kpiLoading,
      activeFiltersCount,
      periodLabel,
      periodPresets,
      kpiCards,
      clusteredMarkers,
      anchorMarkers,
      clusterOptions,
      markerTooltipOptions,
      selectTab,
      setDatePreset,
      onCustomDateChange,
      chooseLayer,
      updateReport,
      resetFilters,
      onAddressToggle,
      toggleAddresses,
      toggleMapFullscreen,
      loadMoreAddresses,
      formatPointDate,
      formatPointTime,
      formatSpeed,
      pointAddress,
      routeIcon,
      markerZIndex,
      focusPoint,
      copyToClipboard,
      handleImageError,
      goBack,
      goToAssetSummary,
      exportLocationHistoryToExcel,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '~leaflet.markercluster/dist/MarkerCluster.css';

.board-diary-report-wrapper {
  .spin-icon {
    animation: board-diary-spin 1s linear infinite;
  }

  @keyframes board-diary-spin {
    100% {
      transform: rotate(360deg);
    }
  }

  .min-width-0 {
    min-width: 0;
  }

  .asset-header-card {
    border: 1px solid rgba(115, 103, 240, 0.14) !important;
    border-left: 4px solid #7367f0 !important;
    border-radius: 0.5rem;
    background-color: #fff;

    .asset-header-content {
      min-height: 64px;
      display: flex;
      align-items: center;
    }

    .back-btn {
      border: 1px solid rgba(0, 0, 0, 0.08);
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(115, 103, 240, 0.1) !important;
        color: #7367f0 !important;
        border-color: #7367f0;
      }
    }

    .asset-avatar-box {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      background: linear-gradient(135deg, #f8f8f8 0%, #eceef2 100%);
      border: 1px solid rgba(0, 0, 0, 0.06);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      .asset-avatar-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        padding: 4px;
      }
    }

    .license-plate-badge {
      display: inline-flex;
      align-items: center;
      background: #fff;
      border: 2px solid #2b2b2b;
      border-radius: 6px;
      padding: 2px 8px;
      font-size: 0.95rem;
      font-weight: 800;
      letter-spacing: 1px;
      color: #2b2b2b;

      .plate-number {
        font-family: Consolas, 'Courier New', monospace;
      }

      &:hover {
        border-color: #7367f0;
        color: #7367f0;
      }
    }

    .asset-brand-text {
      font-size: 1.05rem;
      color: #7367f0 !important;
    }

    .asset-model-title {
      font-size: 1.05rem;
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      background: #f8f8f8;
      border: 1px solid #ebe9f1;
      border-radius: 20px;
      padding: 4px 10px;
      font-size: 0.8rem;

      .meta-label {
        font-weight: 600;
        color: #6e6b7b;
        margin-right: 4px;
      }

      .meta-value {
        font-weight: 600;
        color: #5e5873;
      }
    }

    .btn-view-details {
      font-weight: 600;
      border-radius: 6px;
    }
  }

  .view-switcher {
    display: inline-flex;
    padding: 4px;
    border-radius: 0.5rem;
    background: #f3f2f7;
    gap: 4px;

    button {
      border: 0;
      background: transparent;
      border-radius: 0.4rem;
      padding: 0.45rem 0.9rem;
      font-weight: 600;
      color: #6e6b7b;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      &.active {
        background: #fff;
        color: #7367f0;
        box-shadow: 0 2px 8px rgba(34, 41, 47, 0.08);
      }
    }
  }

  .filters-card,
  .map-card,
  .address-card,
  .kpi-card {
    border-radius: 0.5rem;
  }

  .kpi-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }

    .kpi-unit {
      font-size: 0.8rem;
      font-weight: 500;
      color: #b9b9c3;
    }
  }

  .section-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid #ebe9f1;
  }

  .map-legend {
    gap: 6px;
  }

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    margin-left: 8px;
    border: 2px solid #fff;
    box-shadow: 0 0 0 1px rgba(34, 41, 47, 0.08);

    &.legend-start {
      background: #28c76f;
    }

    &.legend-end {
      background: #ea5455;
    }
  }

  .legend-cluster {
    min-width: 18px;
    height: 18px;
    margin-left: 8px;
    padding: 0 4px;
    border-radius: 999px;
    background: #7367f0;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 1px rgba(34, 41, 47, 0.08);
  }

  .map-stage {
    position: relative;
    min-height: 420px;
  }

  .map-status {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0.4rem 0.75rem;
    border-radius: 999px;
    background: #fff;
    box-shadow: 0 4px 16px rgba(34, 41, 47, 0.12);
    font-size: 0.8rem;
    font-weight: 600;
  }

  .map-empty {
    position: absolute;
    inset: 0;
    z-index: 450;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.88);
  }

  .map-cluster-hint {
    position: absolute;
    left: 12px;
    bottom: 18px;
    z-index: 500;
    max-width: min(280px, calc(100% - 24px));
    padding: 0.4rem 0.7rem;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 4px 16px rgba(34, 41, 47, 0.14);
    color: #5e5873;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.35;
    pointer-events: none;
  }

  .address-scroll {
    max-height: 62vh;
    overflow-y: auto;
    padding: 0.75rem 1rem 1rem;
  }

  .route-stop {
    width: 100%;
    display: flex;
    gap: 0.75rem;
    text-align: left;
    border: 1px solid #ebe9f1;
    background: #fff;
    border-radius: 0.5rem;
    padding: 0.7rem 0.75rem;
    margin-bottom: 0.5rem;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &.active {
      border-color: #7367f0;
      box-shadow: 0 2px 8px rgba(115, 103, 240, 0.12);
    }
  }

  .route-stop-index {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #7367f0;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;

    &.is-start {
      background: #28c76f;
    }

    &.is-end {
      background: #ea5455;
    }
  }

  .address-line {
    color: #5e5873;
    line-height: 1.35;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.18s ease;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }
}

.board-diary-report-wrapper .route-map.leaflet-container {
  height: 62vh !important;
  min-height: 420px;
  width: 100%;
  border-radius: 0 0 0.5rem 0.5rem;
  z-index: 1;
}

.board-diary-report-wrapper .map-card.is-fullscreen {
  position: fixed !important;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  margin: 0 !important;
  border-radius: 0 !important;
  display: flex;
  flex-direction: column;
  background: #fff;

  .map-stage {
    flex: 1;
    min-height: 0;
  }

  .route-map.leaflet-container {
    position: absolute;
    inset: 0;
    height: 100% !important;
    min-height: 0;
    border-radius: 0;
  }
}

.route-pin,
.route-cluster {
  display: flex !important;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 50%;
  background-image: none !important;
  color: #fff;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}

.route-pin {
  border: 2px solid #fff;
  background: #7367f0;
  font-size: 11px;
  box-shadow: 0 1px 4px rgba(34, 41, 47, 0.35);

  span {
    pointer-events: none;
  }
}

.route-pin-dot {
  border-width: 1.5px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.75);
}

.route-pin-start {
  background: #28c76f;
}

.route-pin-end {
  background: #ea5455;
}

.route-pin-selected {
  background: #ff9f43;
  border-width: 2px;
  box-shadow: 0 0 0 5px rgba(255, 159, 67, 0.35);
}

.route-cluster {
  border: 3px solid #fff;
  background: rgba(115, 103, 240, 0.94);
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.28);
  font-size: 12px;

  span {
    pointer-events: none;
  }
}

.route-cluster-md {
  font-size: 13px;
}

.route-cluster-lg {
  font-size: 14px;
}

.leaflet-cluster-spider-leg {
  stroke: #7367f0;
}

body.dark-layout .board-diary-report-wrapper {
  .asset-header-card {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.08) !important;
    border-left-color: #7367f0 !important;

    .back-btn {
      border-color: rgba(255, 255, 255, 0.12);
      color: #b4b7bd;
    }

    .asset-avatar-box {
      background: linear-gradient(135deg, #1e2433 0%, #283046 100%);
      border-color: rgba(255, 255, 255, 0.1);
    }

    .license-plate-badge {
      background: #161d31;
      border-color: #4b4b5a;
      color: #d0d2d6;
    }

    .asset-model-title {
      color: #d0d2d6 !important;
    }

    .meta-chip {
      background: #161d31;
      border-color: rgba(255, 255, 255, 0.08);

      .meta-label {
        color: #b4b7bd;
      }

      .meta-value {
        color: #d0d2d6;
      }
    }
  }

  .view-switcher {
    background: #161d31;

    button.active {
      background: #283046;
    }
  }

  .section-card-header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .map-card.is-fullscreen {
    background: #283046;
  }

  .map-status,
  .map-empty,
  .map-cluster-hint,
  .route-stop {
    background: #283046;
    color: #d0d2d6;
  }

  .route-stop {
    border-color: rgba(255, 255, 255, 0.08);
  }

  .address-line {
    color: #b4b7bd;
  }
}
</style>
